import { RootState } from '@/app/store';
import { camelizeNestedKeys } from '@cex/shared-modules';
import { createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { mainEmptySplitApi } from '../main/mainApi';
import { Capacitor } from '@capacitor/core';
import { downloadInApp, downloadWeb } from '@/shared/helpers/download';
import { CustodyReport, CustodyReportsEntityResponse, CustodyReportsResponse } from '@/types/reports';

const custodyReportsAdapter = createEntityAdapter<CustodyReport>();

const initialState = custodyReportsAdapter.getInitialState();

export const custodyReportsApi = mainEmptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    generateCustodyReports: builder.mutation<CustodyReportsResponse, void>({
      query: () => {
        return {
          url: `/api/v1/custody_reports`,
          method: 'POST'
        };
      },
      invalidatesTags: ['GenerateCustodyReports'],
      transformResponse: (response: { data: CustodyReport }) => {
        const data = camelizeNestedKeys(response.data);
        return data;
      }
    }),
    getCustodyReports: builder.query<CustodyReportsEntityResponse, void>({
      query: () => `/api/v1/custody_reports`,
      providesTags: ['CustodyReports'],
      transformResponse: (response: { data: CustodyReport[] }) => {
        const data = camelizeNestedKeys(response.data);
        return custodyReportsAdapter.setAll(initialState, data);
      }
    }),
    custodyReportsDownload: builder.mutation<any, { id: string; startsAt: string; endsAt: string }>({
      queryFn: async ({ id, startsAt, endsAt }, api, extraOptions, baseQuery) => {
        const isApp = Capacitor.isNativePlatform();

        const result = await baseQuery({
          url: `/api/v1/custody_reports/${id}/download`,
          responseHandler: (response) => response.blob(),
          cache: 'no-cache'
        });

        const contentType = (result.meta as { response?: Response })?.response?.headers.get('content-type') || '';

        let fileExtension: 'pdf' | 'zip' = 'pdf';
        if (contentType.includes('application/zip')) {
          fileExtension = 'zip';
        } else if (contentType.includes('application/pdf')) {
          fileExtension = 'pdf';
        }

        if (isApp) {
          await downloadInApp(`Custody-Report-${startsAt}-${endsAt}`, fileExtension, result.data);
        } else {
          downloadWeb(`Custody-Report-${startsAt}-${endsAt}`, fileExtension, result.data as Blob | MediaSource);
        }

        return { data: null };
      }
    })
  })
});

export const selectGetCustodyReportsResult = custodyReportsApi.endpoints.getCustodyReports.select();
const selectCustodyReportsData = createSelector(selectGetCustodyReportsResult, (apiSlice) => apiSlice.data);

export const { selectAll: selectAllCustodyReports, selectById: selectCustodyReportsById } =
  custodyReportsAdapter.getSelectors((state: RootState) => selectCustodyReportsData(state) ?? initialState);

export const { useGenerateCustodyReportsMutation, useGetCustodyReportsQuery, useCustodyReportsDownloadMutation } =
  custodyReportsApi;
